import Vue from "vue";
import VueRouter, { Route, RouteConfig } from "vue-router";

Vue.use(VueRouter);

export type RouteAuthConfig = { allowAnonymous?: boolean; roles?: string[] };
export type PortalRouteConfig = { auth?: RouteAuthConfig } & RouteConfig;

export const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
});

import { Auth, PortalModule } from "@/@core";

export function initRouter(...modules: PortalModule[]) {
  const routes = [
    ...modules.reduce((routes, module) => {
      routes.push(
        ...module.routes.map(r => {
          if (r.auth) {
            r.meta = r.meta || {};
            r.meta.auth = r.auth;
          }
          return r;
        })
      );
      return routes;
    }, [] as PortalRouteConfig[]),
    {
      path: "/*",
      redirect: modules[0].routes[0].path,
      auth: { allowAnonymous: true },
    },
  ];

  router.addRoutes(routes);

  router.beforeEach(async (to, from, next) => {
    if (!allowsAnonymous(to)) {
      if (!(await Auth.ensureAuthenticated())) {
        return; // break-out of it, `Auth.ensureAuthenticated` has set `window.location.href`
      }
    }

    if (!isAuthorized(to)) {
      return next("/forbidden");
    }

    return next();
  });

  return router;
}

function allowsAnonymous(route: Route) {
  return matchesAuth(route, auth => (auth && auth.allowAnonymous) || false);
}

function isAuthorized(route: Route) {
  return matchesAuth(route, auth => !auth || !auth.roles || auth.roles.length == 0 || Auth.isInRole(...auth.roles));
}

function matchesAuth(route: Route, predicate: (auth: RouteAuthConfig | null) => boolean) {
  return route.matched.some(r => {
    const auth = r.meta?.auth as RouteAuthConfig;
    return predicate(auth);
  });
}
